/**
 * INSTR-016 — Le contrat des liens d'action : types de bout en bout + transport.
 *
 * Tout ce qui décrit une demande (ce qu'on envoie, ce qu'on reçoit) vit ici,
 * ainsi que les deux constantes de transport partagées par les hooks du module.
 *
 * ⚠️ Ces symboles sont ré-exportés par `useInstructorActionLinks.ts`, qui reste
 * le POINT D'ENTRÉE : c'est de là que les 12 composants et les 10 fichiers de
 * test importent. Ne jamais faire pointer un appelant vers ce fichier-ci — les
 * tests qui posent `vi.mock('@/hooks/useInstructorActionLinks')` ne mordraient
 * plus sur lui.
 */

export const ENDPOINT = '/api/instructors'

export function headers(entityId: string): HeadersInit {
    return { 'x-entity-id': entityId, 'content-type': 'application/json' }
}

/** Les 6 statuts du CHECK SQL (mig 337). Union littérale : un statut inconnu
 *  doit casser à la compilation, pas produire une ligne muette à l'écran. */
export type ActionLinkStatus =
    | 'ACTIVE'
    | 'SUBMITTED'
    | 'VALIDATED'
    | 'EXPIRED'
    | 'REVOKED'
    | 'PURGED'

export interface ActionLinkQueueItem {
    id: string
    kind: 'PROOF_REQUEST' | 'SELF_DECLARATION' | 'REQUIREMENT_QUIZ'
    status: ActionLinkStatus
    instructor_id: string | null
    instructor_name: string | null
    instructor_first_name: string | null
    /** Le nom de la cellule « Destinataire » (D155) : le nom UNIQUE de la fiche
     *  (un organisme par sa raison sociale), sinon le nom déclaré ; `null` s'il
     *  n'est pas connu. Servi par la liste des demandes. */
    recipient_label?: string | null
    recipient_email?: string | null
    /** L'adresse e-mail seule (D171), colonne « Adresse e-mail » masquée d'usine.
     *  Clé distincte de `recipient_email`, qui porte le tri et le filtre de la
     *  cellule « Destinataire » (le nom, sinon l'adresse). `null` après le
     *  nettoyage J+30 d'une demande close. */
    recipient_address?: string | null
    created_at?: string | null
    submitted_at: string | null
    expires_at: string | null
    reminder_count: number
    resend_count?: number
    last_resent_at?: string | null
    replaced_by_id?: string | null
    /** Géographie : adresse de la fiche si elle existe, sinon adresse DÉCLARÉE
     *  dans le dossier. Vide pour une invitation restée sans réponse — on ne
     *  sait alors rien de la personne, et on ne l'invente pas. */
    zip_code?: string | null
    city?: string | null
    department_code?: string | null
    phone?: string | null
    latitude?: number | null
    longitude?: number | null
    /** Volumétrie du dossier soumis (formations cochées, pièces jointes). */
    declared_products?: number
    declared_files?: number
    /** D195 — nom au référentiel de chaque exigence qu'une auto-déclaration déposée cite
     *  (`submission.data.requirements`), lu dans le périmètre de l'entité ; une exigence
     *  retirée n'y est pas. */
    requirement_names?: Record<string, string>
    payload: Record<string, unknown> | null
    submission: {
        data?: Record<string, unknown>
        _files?: { s3_key: string; original_name: string; content_type: string; size_bytes: number }[]
        /** PASS-007 — un questionnaire ne se DÉPOSE pas : il n'a ni `data` ni
         *  `_files`, mais une copie notée par exigence. */
        quiz_results?: QuizResult[]
        passed_count?: number
        failed_count?: number
    } | null
}

/** Le verdict d'UNE copie, tel que l'organisme le lit avant de trancher. */
export interface QuizResult {
    attempt_id: string
    requirement_id: string
    requirement_name: string
    quiz_id: string
    /** `null` = aucun verdict prononcé (questionnaire sans base notable). On
     *  n'affiche alors ni « reçu » ni « non reçu » : les deux seraient inventés. */
    passed: boolean | null
    score_percentage: number | null
}

/** Une question de la copie, telle que l'organisme la relit AVANT de trancher. */
export interface QuizAnswerReview {
    question_ref: string
    position: number
    title: string
    type: string
    points: number
    is_eliminatory: boolean
    is_correct: boolean | null
    points_awarded: number | null
    /** Libellés — jamais des identifiants : on relit une copie, pas un journal. */
    given: string[]
    expected: string[]
    value_text: string | null
}

/** Ce qui empêcherait d'inviter cette adresse (pré-check à la saisie). */
export interface RecipientCheck {
    pending_request: {
        id: string
        status: ActionLinkStatus
        created_at: string | null
        expires_at: string | null
        reminder_count: number
        resend_count: number
        recipient_email: string | null
    } | null
    existing_instructors: { id: string; display_name: string; is_active: boolean }[]
}

export interface ActionLinksSummary {
    total: number
    /** Dépôts à valider, TOUS types de demande confondus (dossier
     *  d'auto-déclaration, pièce justificative, questionnaire d'habilitation).
     *  C'est la grandeur des surfaces qui montrent les trois : pastille de la
     *  nav, segment « À valider », et l'écart « page vs total » de la file. */
    submitted: number
    /** Sous-ensemble : les seuls dépôts qui n'ont pas encore de fiche, donc les
     *  seuls dont on puisse dire « vérifiez-les avant de CRÉER leur fiche ».
     *  Réservé au bandeau du vivier — voir `PendingSubmissionsBanner`. */
    submitted_self_declaration: number
    active: number
}

export interface PublicPendingItem {
    item_type: 'REQUIREMENT' | 'COMPLIANCE'
    item_ref: string
    label: string
    status: string
    expires_on: string | null
}

export interface PublicActionLinkView {
    /** `READY` = formulaire servi. `ALREADY_SUBMITTED` / `EXPIRED` = état terminal
     *  rendu à l'identité de l'organisme, sans aucune donnée du dossier. */
    /** `NO_LONGER_NEEDED` : le lien est valide mais la demande n'a plus lieu
     *  d'être (fiche désactivée ou anonymisée). Sans cet état, la page
     *  recalculait une liste VIDE et affichait « tout est à jour » — le lien
     *  mentait poliment, et la personne repartait rassurée à tort. */
    state?: 'READY' | 'ALREADY_SUBMITTED' | 'EXPIRED' | 'NO_LONGER_NEEDED'
    /** Référence STABLE du lien (l'`ial_id`) : clé du brouillon local, qui doit
     *  survivre aux rotations de jeton provoquées par les relances. */
    link_ref?: string
    submitted_at?: string | null
    kind: 'PROOF_REQUEST' | 'SELF_DECLARATION' | 'REQUIREMENT_QUIZ'
    of_name: string
    /** Branding de l'organisme émetteur — la page publique porte SES couleurs
     *  (premier contact intervenant externe ↔ donneur d'ordre). */
    of_logo_url?: string | null
    of_primary_color?: string | null
    expires_at: string
    /** Le jour de l'ORGANISME (`YYYY-MM-DD`, D183) : il borne une date vécue
     *  (obtention, émission) — le dépôt la refuse au-delà. Absent d'un état terminal. */
    today?: string
    // PROOF_REQUEST
    first_name?: string
    items?: PublicPendingItem[]
    // SELF_DECLARATION
    proposed_nature?: string | null
    /** Adresse à laquelle le lien a été envoyé — pré-remplit le champ. */
    invited_email?: string | null
    catalog?: { id: string; name: string; sector: string | null; family: string | null; group: string | null }[]
    product_requirements?: {
        product_id: string
        requirement_id: string
        requirement_name: string
        description?: string | null
        /** Preuve à renouveler (validité limitée) → une date est demandée. */
        has_validity?: boolean
        validity_months?: number | null
    }[]
    // REQUIREMENT_QUIZ (PASS-007)
    requirements?: PublicQuizRequirement[]
}

/** Une option de réponse, telle que servie au répondant.
 *  ⚠️ `is_correct` N'EXISTE PAS ici, et ce n'est pas un oubli : le serveur
 *  retire les bonnes réponses du formulaire public. Les rajouter au type
 *  laisserait croire qu'on peut corriger côté navigateur. */
export interface PublicQuizOption {
    ref: string
    content: string
    image_url?: string | null
}

export interface PublicQuizQuestion {
    ref: string
    position: number
    title: string
    content?: string | null
    type: 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'SHORT_ANSWER'
    image_url?: string | null
    /** `null` quand la composition a demandé de masquer le barème. */
    points?: number | null
    is_eliminatory?: boolean
    options: PublicQuizOption[]
}

/** Un questionnaire = une exigence. Un lien en porte N (PASS-007 §0.4). */
export interface PublicQuizRequirement {
    requirement_id: string
    requirement_name: string
    expires_on?: string | null
    attempt_id: string
    attempt_number: number
    form: {
        quiz_name?: string | null
        /** Durée annoncée, DÉRIVÉE du nombre de questions par le serveur — jamais
         *  la limite de composition, qui n'est pas appliquée et annonçait
         *  « 10 minutes » pour quatre questions à cocher. `null` quand la
         *  composition demande de ne pas parler de temps (`show_timer`). */
        estimated_minutes?: number | null
        questions: PublicQuizQuestion[]
    }
    /** Réponses DÉJÀ enregistrées — la reprise après coupure. */
    answers: { question_ref: string; value_choice: string[]; value_text?: string | null }[]
}
